import React, { useEffect, useRef, useState } from 'react';
import { Button, Select, Spin, Tooltip } from 'antd';
import {
  CloseOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  TagsOutlined,
} from '@ant-design/icons';

import { errorMessage, findTags, resolveTag } from './api';
import { NewsTag, TagChoice } from './types';
import { canonicalChoice, tagChoiceKey, tagLabel } from './tags';

type Props = {
  choices: TagChoice[];
  active: TagChoice | null;
  onSelect: (tag: TagChoice | null) => void;
  onAdd: (tag: NewsTag) => void;
};

const TagPalette: React.FC<Props> = ({ choices, active, onSelect, onAdd }) => {
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<NewsTag[]>([]);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!adding) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    setError('');
    setRows([]);
    setNextPage(null);
    const timer = window.setTimeout(
      async () => {
        try {
          const page = await findTags(query.trim(), 1, controller.signal);
          if (controller.signal.aborted) return;
          setRows(page.results);
          setNextPage(page.next_page);
        } catch (reason) {
          if (!controller.signal.aborted)
            setError(errorMessage(reason) || 'Tags could not be loaded.');
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      },
      query ? 250 : 0,
    );
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [adding, query, attempt]);

  const loadMore = async () => {
    if (!nextPage || loading) return;
    const controller = request.current;
    if (!controller || controller.signal.aborted) return;
    setLoading(true);
    try {
      const page = await findTags(query.trim(), nextPage, controller.signal);
      if (controller.signal.aborted) return;
      setRows(current =>
        Array.from(
          new Map(
            [...current, ...page.results].map(tag => [tag.id, tag]),
          ).values(),
        ),
      );
      setNextPage(page.next_page);
    } catch (reason) {
      if (!controller.signal.aborted) setError(errorMessage(reason));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const choose = (tag: NewsTag) => {
    const canonical = canonicalChoice(tag);
    onAdd(canonical);
    onSelect(canonical);
    setQuery('');
    setAdding(false);
    setError('');
  };

  const create = async () => {
    if (!query.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      choose(await resolveTag(query.trim()));
    } catch (reason) {
      setError(errorMessage(reason) || 'This tag could not be added.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="market-explorer__tag-palette"
      aria-label="News tagging"
      onKeyDown={event => {
        if (event.key === 'Escape') onSelect(null);
      }}
    >
      <div className="market-explorer__palette-heading">
        <span>
          <TagsOutlined /> Tag news
        </span>
        <Tooltip title="Choose a tag, then click news rows to apply it. Suggested instrument and regime tags are added to your palette only; each story is tagged only when you click it. Saved tags are shared across PM.">
          <button
            type="button"
            className="market-explorer__info"
            aria-label="About tagging news"
          >
            <InfoCircleOutlined />
          </button>
        </Tooltip>
      </div>
      <div className="market-explorer__palette-tags">
        {choices.map(tag => {
          const selected = active && tagChoiceKey(active) === tagChoiceKey(tag);
          const label = tagLabel(tag);
          return (
            <button
              type="button"
              key={tagChoiceKey(tag)}
              className={`market-explorer__palette-tag ${selected ? 'is-active' : ''}`}
              aria-pressed={Boolean(selected)}
              aria-label={`Tag with ${label}`}
              onClick={() => onSelect(selected ? null : tag)}
            >
              {label}
            </button>
          );
        })}
        <Button
          type="text"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setAdding(!adding)}
          aria-expanded={adding}
          aria-label="Add tag"
        >
          Add tag
        </Button>
      </div>
      {adding && (
        <div className="market-explorer__tag-search">
          <Select
            aria-label="Search existing tags"
            showSearch
            filterOption={false}
            placeholder="Search existing tags or type a new tag"
            value={undefined}
            searchValue={query}
            onSearch={setQuery}
            loading={loading}
            disabled={saving}
            options={rows.map(tag => ({ value: tag.id, label: tagLabel(tag) }))}
            onSelect={id => {
              const tag = rows.find(item => item.id === id);
              if (tag) choose(tag);
            }}
            notFoundContent={
              loading ? <Spin size="small" /> : error || 'No matching tags'
            }
            popupRender={menu => (
              <>
                {menu}
                {nextPage && (
                  <Button
                    type="link"
                    size="small"
                    loading={loading}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => void loadMore()}
                  >
                    Load more tags
                  </Button>
                )}
              </>
            )}
          />
          {query.trim() && (
            <Button
              onClick={() => void create()}
              loading={saving}
              icon={<PlusOutlined />}
              aria-label={`Add “${query.trim()}”`}
            >
              Add “{query.trim()}”
            </Button>
          )}
          <Button
            type="text"
            icon={<CloseOutlined />}
            aria-label="Close tag search"
            onClick={() => setAdding(false)}
            disabled={saving}
          />
          {error && (
            <div role="alert" className="market-explorer__row-error">
              {error}{' '}
              <Button
                type="link"
                size="small"
                onClick={() => setAttempt(value => value + 1)}
              >
                Retry search
              </Button>
            </div>
          )}
        </div>
      )}
      <div className={`market-explorer__tag-hint ${active ? 'is-active' : ''}`}>
        {active ? (
          <>
            <span>
              Click stories to apply <strong>{tagLabel(active)}</strong>.
            </span>
            <Button type="link" size="small" onClick={() => onSelect(null)}>
              Done tagging
            </Button>
          </>
        ) : (
          <span>Choose a tag, then click stories to apply it.</span>
        )}
      </div>
    </div>
  );
};

export default TagPalette;
